import { useState } from "react";
import { BellIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import PlanGatedPage from "../components/PlanGatedPage";

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  subject: 200,
  message: 1000,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns but preserve formatting
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  
  return sanitized;
};

export default function PushNotifications() {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  
  // State
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Handle send push notification
  const handleSend = async () => {
    // Validation
    if (!subject.trim()) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a subject",
      });
      return;
    }

    if (!message.trim()) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a message",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before sending another notification",
      });
      return;
    }

    setLastSubmissionTime(now);
    setSending(true);

    const authToken = getAuthToken();
    if (!authToken) {
      setNotification({
        show: true,
        message: "Authentication required",
        title: "Error",
      });
      setSending(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/send-push-notification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            subject: sanitizeInput(subject),
            message: sanitizeInput(message),
            studio_id: studioUser?.studio_id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send push notification");
      }

      const result = await response.json();

      setNotification({
        show: true,
        title: "Success",
        message: result.message || `Notification sent to ${result.successCount} devices`,
      });

      // Clear form
      setSubject("");
      setMessage("");
    } catch (error: any) {
      console.error('Error sending push notification:', error);
      setNotification({
        show: true,
        message: error.message || "Failed to send push notification. Please try again.",
        title: "Error",
      });
    } finally {
      setSending(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSubject("");
    setMessage("");
    setNotification({
      show: true,
      message: "Notification discarded",
      title: "Cancelled",
    });
  };

  return (
    <PlanGatedPage
      capability="messagingPush"
      title="Push Notifications"
      subtitle="Send push notifications to parents using the DojoTrack app"
    >
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white 2xl:pt-6">
              Push Notifications
            </h1>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Subject Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Subject
              </label>
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value);
                if (sanitizedValue.length <= MAX_LENGTHS.subject) {
                  setSubject(sanitizedValue);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter notification subject..."
              maxLength={MAX_LENGTHS.subject}
            />
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-right">
              {subject.length}/{MAX_LENGTHS.subject}
            </div>
          </div>

          {/* Message Section */}
          <div className="p-4">
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Message
              </label>
            </div>
            <textarea
              value={message}
              onChange={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value);
                if (sanitizedValue.length <= MAX_LENGTHS.message) {
                  setMessage(sanitizedValue);
                }
              }}
              className="w-full h-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              placeholder="Type your push notification message here..."
              maxLength={MAX_LENGTHS.message}
            />
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-right">
              {message.length}/{MAX_LENGTHS.message}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !message.trim()}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2 pointer-events-none" />
                    <span className="pointer-events-none">Send</span>
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={sending}
                className="inline-flex items-center px-6 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="pointer-events-none">Cancel</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </PlanGatedPage>
  );
}
